import { RELATIONSHIP_TYPES } from "@tellescope/constants";
import type { EnduserRelationship } from "@tellescope/types-models";
import { useForm } from "../../../use-form";
import {
  Button,
  DatePicker,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";

const convertStringToRelationship = (str: string): EnduserRelationship["type"] | undefined =>
  RELATIONSHIP_TYPES.find((type) => type === str);

export interface RelatedContactFormResult {
  fname: string;
  lname?: string;
  relationship: EnduserRelationship["type"];
  dateOfBirth?: Date;
  email: string;
  phoneNumber?: string;
}

export interface AddRelatedContactFormProps {
  initialContact?: RelatedContactFormResult;
  onSave: (contact: RelatedContactFormResult) => void;
  onCancel?: () => void;
  className?: string;
  onBlur?: () => void;
}

export const AddRelatedContactForm = ({
  initialContact,
  onSave,
  onCancel,
  className,
  onBlur,
}: AddRelatedContactFormProps) => {
  const { t } = useTranslation("forms");
  const form = useForm({
    fields: {
      fname: {
        type: "text",
        initialValue: initialContact?.fname,
        required: true,
      },
      lname: {
        type: "text",
        initialValue: initialContact?.lname,
      },
      relationship: {
        type: "select",
        initialValue: initialContact?.relationship ?? "",
        required: true,
      },
      dateOfBirth: {
        type: "date",
        initialValue: initialContact?.dateOfBirth,
      },
      email: {
        type: "email",
        initialValue: initialContact?.email,
        required: true,
      },
      phoneNumber: {
        type: "phone",
        initialValue: initialContact?.phoneNumber,
      },
    },
    validateOn: "blur",
  });

  const getFieldProps: typeof form.getFieldProps = (fieldName) => {
    const props = form.getFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  const getCustomFieldProps: typeof form.getCustomFieldProps = (fieldName) => {
    const props = form.getCustomFieldProps(fieldName);
    return {
      ...props,
      onBlur: () => {
        props.onBlur();
        onBlur?.();
      },
    };
  };

  return (
    <div className="grid grid-cols-2 gap-2" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
      <Input {...getFieldProps("fname")} placeholder={t("First Name")} />
      <Input {...getFieldProps("lname")} placeholder={t("Last Name")} />
      <Select {...getCustomFieldProps("relationship")}>
        <SelectTrigger className={className}>
          <SelectValue placeholder={t("Relationship")}>{form.values.relationship}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {RELATIONSHIP_TYPES.map((relationship) => (
            <SelectItem key={relationship} value={relationship}>
              {relationship}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DatePicker {...getCustomFieldProps("dateOfBirth")} placeholder={t("Date of Birth")} label="" />
      <Input {...getFieldProps("email")} type="email" placeholder={t("Email")} />
      <PhoneInput {...getCustomFieldProps("phoneNumber")} placeholder={t("Phone Number")} />
      <div className="flex col-span-3 justify-end gap-2">
        {!!onCancel && (
          <Button
            variant="outline"
            onClick={() => {
              onCancel();
            }}
          >
            {t("Cancel")}
          </Button>
        )}
        <Button
          disabled={!form.isValid}
          onClick={() => {
            const relationship = convertStringToRelationship(form.values.relationship);
            if (!relationship) return;

            onSave({
              ...form.values,
              relationship,
            });
          }}
        >
          {t("Save Contact")}
        </Button>
      </div>
    </div>
  );
};
